import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';
import { useRecapSidebarCollapse } from '@confluence/confluence-recap/entry-points/useRecapSidebarCollapse.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export const DEFAULT_SIDEBAR_WIDTH = 320;

export const useSideNavInitialState = () => {
	const shouldCollapseForRecap = useRecapSidebarCollapse();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { userId } = useSessionData();
	const isAnonymousUser = !userId;

	if (isStableUIEnabledForTests()) {
		return {
			initialCollapsedState: 'expanded',
			initialWidth: DEFAULT_SIDEBAR_WIDTH,
			shouldForceExpand: true,
			needsPostHydrationCollapse: false,
		};
	}

	const shouldForceExpand = false;

	const persistedCollapseState =
		confluenceLocalStorageInstance.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
		) ?? 'expanded';
	let persistedWidth = parseInt(
		confluenceLocalStorageInstance.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH, //can return null does not handle string
		) || DEFAULT_SIDEBAR_WIDTH,
		10,
	);

	if (Number.isNaN(persistedWidth)) {
		//fix for if PERSISTED_LEFT_SIDEBAR_WIDTH is somehow a string
		persistedWidth = DEFAULT_SIDEBAR_WIDTH;
	}

	const isWidthTooLarge = persistedWidth > window.innerWidth;
	const fittedSidebarWidth = isWidthTooLarge ? DEFAULT_SIDEBAR_WIDTH : persistedWidth;

	// During hydration for anonymous users, default to expanded to match SSR output
	// since anonymous users don't have server-persisted localStorage. After hydration
	// completes, the sidebar will update to reflect the user's localStorage preference.
	if (
		isAnonymousUser &&
		renderServerPlaceholder &&
		expVal('confluence_frontend_sidebar_hydration_fix', 'isEnabled', false)
	) {
		return {
			initialCollapsedState: 'expanded' as const,
			initialWidth: fittedSidebarWidth,
			shouldForceExpand,
			needsPostHydrationCollapse: persistedCollapseState === 'collapsed',
		};
	}

	let initialCollapsedState: 'collapsed' | 'expanded';
	if (shouldForceExpand) {
		initialCollapsedState = 'expanded';
	} else if (shouldCollapseForRecap) {
		initialCollapsedState = 'collapsed';
	} else {
		initialCollapsedState = persistedCollapseState;
	}

	return {
		initialCollapsedState,
		initialWidth: fittedSidebarWidth,
		shouldForceExpand,
		needsPostHydrationCollapse: false,
	};
};
